import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchFacets } from '@app/src/api/fetchHooks'
import useProviderType from '@app/src/hooks/useProviderType'
import useSignalRDataListener from '@app/src/hooks/useSignalRDataListener'
import { CONFIGURATION_PAGE_IDS } from '@app/src/pages/Configurations/ConfigurationsScene'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import ImportEnrichingView from '@app/src/pages/ResourceCollection/Collections/Provider/ConnectProviders/ImportEnrichingView'
import { useScheduleEnrichment } from '@app/src/pages/ResourceCollection/Collections/Provider/ScheduleEnrichment/hooks/scheduleEnrichment'
import type { ScheduleEnrichmentFormData } from '@app/src/pages/ResourceCollection/Collections/Provider/ScheduleEnrichment/ScheduleEnrichmentModal'
import { SCHEDULE_ENRICHMENT_QUESTION_KEYS } from '@app/src/pages/ResourceCollection/Collections/Provider/ScheduleEnrichment/ScheduleEnrichmentModal'
import {
  TabularImportEntityProvider,
  useTabularImportEntity,
} from '@app/src/pages/TabularImport/shared/TabularImportEntityContext'
import {
  invalidateDedup,
  TabularImportQueryKey,
  useApproveCurrentMappings,
  useRetargetMapping,
  useSetTabularImportStatus,
  useSplitMapping,
  useStartDeduplication,
  useStartTabularImport,
  useTabularImportStatus,
  useUpdateTabularMappings,
  useUploadTabularImport,
} from '@app/src/pages/TabularImport/shared/tabularImportHooks'
import CompleteStep from '@app/src/pages/TabularImport/shared/components/CompleteStep'
import FailedStep from '@app/src/pages/TabularImport/shared/components/FailedStep'
import DeduplicationReviewStep from '@app/src/pages/TabularImport/shared/components/DeduplicationReviewStep'
import MappingStep from '@app/src/pages/TabularImport/shared/components/MappingStep'
import ProcessingStep from '@app/src/pages/TabularImport/shared/components/ProcessingStep'
import ReviewStep from '@app/src/pages/TabularImport/shared/components/ReviewStep'
import UploadStep from '@app/src/pages/TabularImport/shared/components/UploadStep'
import CategoryMappingsSection from './components/CategoryMappingsSection'
import CategoryPreviewCell from './components/CategoryPreviewCell'
import ConvertToCategoryAction from './components/ConvertToCategoryAction'
import EnrichmentStep from './components/EnrichmentStep'
import ProviderSearchAssign from './components/ProviderSearchAssign'
import {
  useConvertCategoryToColumns,
  useConvertColumnToCategory,
  useNewProviderIds,
  useUpdateCategoryPlan,
} from './providerImportHooks'
import { PROVIDER_FIELD_CONFIGS, PROVIDER_REQUIRED_FIELDS } from './providerFieldConfig'
import type { CategoryPlan } from '@app/src/types/categoryPlan'
import type {
  DeduplicationProgressUpdate as DedupProgress,
  FieldMappingDto,
  ImportProgressUpdate,
  ImportSession,
  ImportStatus,
  TabularImportProgressUpdate,
} from '@app/src/types/tabularImport'
import { isProcessingStatus } from '@app/src/types/tabularImport'
import paths from '@app/src/wf-constants/paths'
import { Box, Button, Dialog, DialogContent, Divider, Stack, Step, StepLabel, Stepper, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useConfirm } from 'material-ui-confirm'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { generatePath, useHistory } from 'react-router-dom'

type TabularImportProvidersModalProps = {
  open: boolean
  onClose: () => void
}

enum ImportStep {
  Upload = 0,
  ColumnMatches = 1,
  Processing = 2,
  Validate = 3,
  Deduplication = 4,
  Complete = 5,
  Enrichment = 6,
  Enriching = 7,
}

function getStepForStatus(status: ImportStatus | undefined): ImportStep {
  if (!status) return ImportStep.Upload
  switch (status) {
    case 'Analyzing':
      return ImportStep.Upload
    case 'AwaitingMappingApproval':
      return ImportStep.ColumnMatches
    case 'MappingApproved':
    case 'Processing':
      return ImportStep.Processing
    case 'AwaitingReview':
      return ImportStep.Validate
    case 'DetectingDuplicates':
    case 'DeduplicationReview':
      return ImportStep.Deduplication
    case 'Importing':
    case 'Imported':
      return ImportStep.Complete
    case 'Failed':
    default:
      return ImportStep.Upload
  }
}

const parseCategoryPlan = (json: string | null | undefined): CategoryPlan | null => {
  if (!json) return null
  try {
    return JSON.parse(json) as CategoryPlan
  } catch {
    return null
  }
}

const TabularImportProvidersModal: React.FC<TabularImportProvidersModalProps> = ({ open, onClose }) => (
  <TabularImportEntityProvider
    entityType="provider"
    fieldConfigs={PROVIDER_FIELD_CONFIGS}
    requiredFields={PROVIDER_REQUIRED_FIELDS}
  >
    <ProvidersModalContent open={open} onClose={onClose} />
  </TabularImportEntityProvider>
)

const PROVIDER_MATCH_DESCRIPTION =
  'Rows are matched against existing providers in priority order: WF ID › Custom ID › Reg. No. › DUNS › Name + Country. A Name + Country match also requires the country to be identical.'

const PROVIDER_UNASSIGNED_HINT = 'Unassigned column. Map it to a field, or make it a category.'

const PROVIDER_UPLOAD_DESCRIPTION =
  'Upload a CSV or Excel (.xlsx) file with provider data. Our AI will automatically detect and map the columns.'

const ProvidersModalContent: React.FC<TabularImportProvidersModalProps> = ({ open, onClose }) => {
  const { formatMessage } = useIntl()
  const { entityType } = useTabularImportEntity()
  const confirm = useConfirm()
  const history = useHistory()
  const [importFile, setImportFile] = useState<string | Blob>('')
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [localMappings, setLocalMappings] = useState<FieldMappingDto[] | null>(null)
  const [realtimeProgress, setRealtimeProgress] = useState<TabularImportProgressUpdate | null>(null)
  const [dedupProgress, setDedupProgress] = useState<DedupProgress | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [categoryPlan, setCategoryPlan] = useState<CategoryPlan | null>(null)
  const [showEnrichmentStep, setShowEnrichmentStep] = useState(false)
  const [enrichmentScheduled, setEnrichmentScheduled] = useState(false)
  const [importProgress, setImportProgress] = useState<Omit<ImportProgressUpdate, 'sessionId'> | null>(null)
  const [importResult, setImportResult] = useState<{
    createdCount: number
    updatedCount: number
    failedCount: number
    warnings: string[]
  } | null>(null)
  const queryClient = useQueryClient()
  const providerType = useProviderType()
  const { scheduleEnrichment, isScheduling: isEnrichmentScheduling } = useScheduleEnrichment()

  // FormProvider is required by EnrichmentInfoStep for email checkbox
  const formMethods = useForm<ScheduleEnrichmentFormData>({
    defaultValues: {
      [SCHEDULE_ENRICHMENT_QUESTION_KEYS.PROVIDER_IDS]: [],
      [SCHEDULE_ENRICHMENT_QUESTION_KEYS.SEND_EMAIL_ON_COMPLETE]: false,
    },
  })

  // Fetch automation facets to check if AI Screen/Enrich automations exist
  const {
    facets: [automations],
  } = useFetchFacets({
    key: [FetchKey.AutomationFacets],
    endpoint: endpoints.automationsWithFacets,
    options: { enabled: open },
    facetsParam: [{ name: 'automationType' }],
    filter: [
      {
        name: 'automationType',
        filters: [
          { value: 'AiScreen', operator: Operators.EqualTo },
          { value: 'AiEnrich', operator: Operators.EqualTo },
        ],
      },
    ],
  })

  const hasAiScreenAutomations = automations?.some(x => x.value === 'AiScreen' && x.count > 0)
  const hasAiEnrichAutomations = automations?.some(x => x.value === 'AiEnrich' && x.count > 0)

  // Dynamic steps array - only include enrichment steps if no AI Enrich automation exists
  const steps = useMemo(() => {
    const baseSteps = ['Upload file', 'Column matches', 'Processing', 'Validate', 'Deduplication', 'Import complete']
    if (!hasAiEnrichAutomations) {
      return [...baseSteps, 'Enrichment', 'Enrichment complete']
    }
    return baseSteps
  }, [hasAiEnrichAutomations])

  const { mutate: uploadFile, isPending: isUploading } = useUploadTabularImport({
    onSuccess: response => {
      setSessionId(response.sessionId)
      setFileError(null)
    },
  })

  const { data: importStatus, refetch: refetchStatus } = useTabularImportStatus(sessionId)

  const { mutate: importProviders, isPending: isImporting } = useStartTabularImport({
    onSuccess: () => {
      refetchStatus()
    },
  })

  const { mutate: updateMappings } = useUpdateTabularMappings()
  const { mutateAsync: retargetMapping } = useRetargetMapping()
  const { mutateAsync: convertColumnToCategory } = useConvertColumnToCategory()
  const { mutateAsync: splitMapping } = useSplitMapping()
  const { mutateAsync: convertCategoryToColumns } = useConvertCategoryToColumns()
  const { mutate: approveCurrentMappings, isPending: isApprovingCurrent } = useApproveCurrentMappings()
  const { mutate: startDeduplication, isPending: isStartingDedup } = useStartDeduplication({
    onSuccess: () => refetchStatus(),
  })
  const { mutate: setImportStatus } = useSetTabularImportStatus({
    onSuccess: () => refetchStatus(),
  })
  const { updateCategoryPlan } = useUpdateCategoryPlan(sessionId)

  // Fetch new provider IDs after import completes (for enrichment)
  const { data: newProviderIds = [] } = useNewProviderIds(
    importResult && importResult.createdCount > 0 ? sessionId : null,
  )

  // Populate importResult from session data on page refresh
  useEffect(() => {
    if (importStatus?.status === 'Imported') {
      setImportResult(
        prev =>
          prev ?? {
            createdCount: importStatus.importedCreatedCount ?? 0,
            updatedCount: importStatus.importedUpdatedCount ?? 0,
            failedCount: importStatus.importedFailedCount ?? 0,
            warnings: importStatus.importWarnings ?? [],
          },
      )
    }
  }, [importStatus])

  // Calculate current step from status, with enrichment step overrides
  const currentStep = useMemo(() => {
    const baseStep = getStepForStatus(importStatus?.status)

    // If we're at Complete and enrichment automation is disabled
    // AND we have new providers (not just updates), show enrichment steps
    if (baseStep === ImportStep.Complete && !hasAiEnrichAutomations && newProviderIds.length > 0) {
      if (showEnrichmentStep) return ImportStep.Enrichment
      if (enrichmentScheduled) return ImportStep.Enriching
    }

    return baseStep
  }, [importStatus?.status, hasAiEnrichAutomations, showEnrichmentStep, enrichmentScheduled, newProviderIds.length])

  // Sync localMappings from server when first received (non-empty)
  React.useEffect(() => {
    if (importStatus?.fieldMappings && importStatus.fieldMappings.length > 0 && localMappings === null) {
      setLocalMappings(importStatus.fieldMappings)
    }
  }, [importStatus?.fieldMappings, localMappings])

  // Parse category plan from status response (only once)
  useEffect(() => {
    if (importStatus?.categoryPlanJson && !categoryPlan) {
      setCategoryPlan(parseCategoryPlan(importStatus.categoryPlanJson))
    }
  }, [importStatus?.categoryPlanJson, categoryPlan])

  // Listen for real-time progress updates via SignalR
  const handleProgressUpdate = useCallback(
    (progress: TabularImportProgressUpdate) => {
      // Only update if this progress is for our session
      if (sessionId && progress.sessionId === sessionId) {
        setRealtimeProgress(progress)
      }
    },
    [sessionId],
  )

  useSignalRDataListener<TabularImportProgressUpdate>(
    'TabularImportProgress',
    handleProgressUpdate,
    open && Boolean(sessionId),
  )

  // Listen for mapping analysis completion via SignalR
  const handleMappingComplete = useCallback(
    (data: { sessionId: string }) => {
      if (sessionId && data.sessionId === sessionId) {
        // Immediately refetch status when mapping analysis completes
        refetchStatus()
      }
    },
    [sessionId, refetchStatus],
  )

  useSignalRDataListener<{ sessionId: string }>(
    'TabularImportMappingComplete',
    handleMappingComplete,
    open && Boolean(sessionId),
  )

  // Listen for dedup progress updates via SignalR
  const handleDedupProgress = useCallback(
    (progress: DedupProgress) => {
      if (sessionId && progress.sessionId === sessionId) {
        setDedupProgress(progress)
      }
    },
    [sessionId],
  )

  useSignalRDataListener<DedupProgress>('DeduplicationProgress', handleDedupProgress, open && Boolean(sessionId))

  const handleDedupComplete = useCallback(
    (data: { sessionId: string }) => {
      if (sessionId && data.sessionId === sessionId) {
        setDedupProgress(null)
        invalidateDedup(queryClient, sessionId)
      }
    },
    [sessionId, queryClient],
  )

  useSignalRDataListener<{ sessionId: string }>('DeduplicationReview', handleDedupComplete, open && Boolean(sessionId))

  // Listen for import progress updates via SignalR
  const handleImportProgress = useCallback(
    (progress: ImportProgressUpdate) => {
      if (sessionId && progress.sessionId === sessionId) {
        const { sessionId: _sid, ...progressData } = progress
        setImportProgress(progressData)
      }
    },
    [sessionId],
  )

  useSignalRDataListener('ImportProgress', handleImportProgress, open && Boolean(sessionId))

  // Listen for import completion via SignalR
  const handleImportComplete = useCallback(
    (data: {
      sessionId: string
      createdCount: number
      updatedCount: number
      failedCount: number
      warnings: string[]
    }) => {
      if (sessionId && data.sessionId === sessionId) {
        setImportProgress(null)
        setImportResult({
          createdCount: data.createdCount,
          updatedCount: data.updatedCount,
          failedCount: data.failedCount,
          warnings: data.warnings ?? [],
        })
        refetchStatus()
        queryClient.invalidateQueries({ queryKey: TabularImportQueryKey.base })
        queryClient.invalidateQueries({ queryKey: TabularImportQueryKey.unfinishedSessions(entityType) })
      }
    },
    [sessionId, refetchStatus, queryClient, entityType],
  )

  useSignalRDataListener('ImportComplete', handleImportComplete, open && Boolean(sessionId))

  // Clear the previous session's progress and result when the session changes or the modal closes:
  // the modal stays mounted, and the effect above only fills importResult in when it is still null.
  React.useEffect(() => {
    if (!open || !sessionId) {
      setRealtimeProgress(null)
      setDedupProgress(null)
      setImportProgress(null)
      setImportResult(null)
    }
  }, [open, sessionId])

  const handleUpload = useCallback(() => {
    if (importFile instanceof Blob) {
      uploadFile(importFile as File)
    }
  }, [importFile, uploadFile])

  const handleFileChange = useCallback<React.Dispatch<React.SetStateAction<string | Blob>>>(file => {
    setImportFile(file)
    setFileError(null)
  }, [])

  const handleFileRejected = useCallback(
    (rejections: { file: File; errors: { code: string; message: string }[] }[]) => {
      if (rejections.length > 0) {
        const fileType = rejections[0].file.name.split('.').pop()?.toUpperCase() || 'Unknown'
        setFileError(
          `File type .${fileType?.toLowerCase()} is not supported. Please upload a CSV or Excel (.xlsx) file.`,
        )
      }
    },
    [],
  )

  const handleToggleMapping = useCallback(
    (mappingId: number) => {
      if (!localMappings || !sessionId) return
      const updated = localMappings.map(m => (m.id === mappingId ? { ...m, isApplied: !m.isApplied } : m))
      setLocalMappings(updated)
      const toggled = updated.find(m => m.id === mappingId)
      if (toggled) {
        updateMappings({ sessionId, mappings: [{ id: mappingId, isApplied: toggled.isApplied }] })
      }
    },
    [localMappings, sessionId, updateMappings],
  )

  const syncMappingsFromStatus = useCallback((status: ImportSession) => {
    if (status.fieldMappings) setLocalMappings(status.fieldMappings)
    setCategoryPlan(parseCategoryPlan(status.categoryPlanJson))
  }, [])

  const handleRetargetMapping = useCallback(
    async (mappingId: number, targetField: string) => {
      if (!sessionId) return
      syncMappingsFromStatus(await retargetMapping({ sessionId, mappingId, targetField }))
    },
    [sessionId, retargetMapping, syncMappingsFromStatus],
  )

  const handleConvertColumnToCategory = useCallback(
    async (mappingId: number, categoryName: string) => {
      if (!sessionId) return
      syncMappingsFromStatus(await convertColumnToCategory({ sessionId, mappingId, categoryName }))
    },
    [sessionId, convertColumnToCategory, syncMappingsFromStatus],
  )

  const handleSplitMapping = useCallback(
    async (mappingId: number) => {
      if (!sessionId) return
      syncMappingsFromStatus(await splitMapping({ sessionId, mappingId }))
    },
    [sessionId, splitMapping, syncMappingsFromStatus],
  )

  const handleConvertCategoryToColumns = useCallback(
    async (sourceColumnNames: string[]) => {
      if (!sessionId) return
      syncMappingsFromStatus(await convertCategoryToColumns({ sessionId, sourceColumnNames }))
    },
    [sessionId, convertCategoryToColumns, syncMappingsFromStatus],
  )

  const handleApproveCurrentMappings = useCallback(() => {
    if (!sessionId) return
    approveCurrentMappings(sessionId, {
      onSuccess: () => refetchStatus(),
    })
  }, [sessionId, approveCurrentMappings, refetchStatus])

  const handleFinalImport = useCallback(async () => {
    if (!sessionId) return

    // Show automation warning if AI Screen automations are enabled
    if (hasAiScreenAutomations) {
      try {
        await confirm({
          title: formatMessage({ id: 'import.importWithAiScreenAutomation.title' }),
          description: formatMessage({ id: 'import.importWithAiScreenAutomation.description' }),
          confirmationText: formatMessage({ id: 'import.continueToImport' }),
          cancellationText: formatMessage({ id: 'import.viewAutomation' }),
          cancellationButtonProps: { variant: 'text' },
        })
      } catch (error) {
        // User clicked "view automation" - close modal and navigate to Automations page
        setImportFile('')
        setSessionId(null)
        setLocalMappings(null)
        setFileError(null)
        setCategoryPlan(null)
        onClose()
        history.push(
          generatePath(paths.configurations, {
            configurationsPage: CONFIGURATION_PAGE_IDS.Automations,
          }),
        )
        return
      }
    }

    importProviders({ sessionId })
  }, [sessionId, importProviders, hasAiScreenAutomations, confirm, formatMessage, onClose, history])

  const handleCloseModal = useCallback(() => {
    setImportFile('')
    setSessionId(null)
    setLocalMappings(null)
    setFileError(null)
    setCategoryPlan(null)
    onClose()
  }, [onClose])

  const handleComplete = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: [FetchKey.ProviderCollection] })
    handleCloseModal()
  }, [queryClient, handleCloseModal])

  const handleShowEnrichmentStep = useCallback(() => {
    // Only show enrichment step if we have NEW provider IDs (not updates)
    if (newProviderIds.length === 0) {
      return
    }
    setShowEnrichmentStep(true)
  }, [newProviderIds])

  const handleScheduleEnrichment = useCallback(
    async (enabled: boolean) => {
      if (!enabled) {
        // User chose not to enrich, go back to complete step
        setShowEnrichmentStep(false)
        return
      }

      // Guard against empty NEW provider IDs (only enrich new providers, not updates)
      if (newProviderIds.length === 0) {
        setShowEnrichmentStep(false)
        return
      }

      // Schedule enrichment for NEW providers only
      try {
        const sendEmailOnComplete = formMethods.getValues(SCHEDULE_ENRICHMENT_QUESTION_KEYS.SEND_EMAIL_ON_COMPLETE)
        await scheduleEnrichment({
          providerIds: newProviderIds,
          sendEmailOnComplete,
        })
        setEnrichmentScheduled(true)
        setShowEnrichmentStep(false)
      } catch (error) {
        // Scheduling failed - silently return to complete step
        setShowEnrichmentStep(false)
      }
    },
    [scheduleEnrichment, newProviderIds, formMethods],
  )

  const handleSkipEnrichment = useCallback(() => {
    // Close modal and go back to suppliers page
    handleComplete()
  }, [handleComplete])

  const handleResumeSession = useCallback((resumeSessionId: string) => {
    setSessionId(resumeSessionId)
    setLocalMappings(null)
  }, [])

  const getProgress = useCallback(() => {
    // Use realtime progress if available, otherwise fall back to API data
    if (realtimeProgress) {
      return (realtimeProgress.processedRows / realtimeProgress.totalRows) * 100
    }
    if (!importStatus) return 0
    return ((importStatus.successfulRows + importStatus.failedRows) / importStatus.totalRows) * 100
  }, [importStatus, realtimeProgress])

  const isProcessing = importStatus && isProcessingStatus(importStatus.status)
  const isAnalyzing = importStatus?.status === 'Analyzing'

  // Auto-select email checkbox when enrichment count >= 20 (only for new providers)
  useEffect(() => {
    if (showEnrichmentStep && newProviderIds.length >= 20) {
      formMethods.setValue(SCHEDULE_ENRICHMENT_QUESTION_KEYS.SEND_EMAIL_ON_COMPLETE, true)
    }
  }, [showEnrichmentStep, newProviderIds.length, formMethods])

  return (
    <Dialog open={open} onClose={handleCloseModal} fullScreen>
      <FormProvider {...formMethods}>
        <DialogContent sx={{ padding: '0px 0px' }}>
          {/* Header */}
          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" px={4} pt={2} mb={2}>
            <Box display="flex" alignItems="center">
              <Typography variant="subtitle1">
                {formatMessage({ id: 'import.provider' }, { providerType })} - AI-Powered Mapping
              </Typography>
            </Box>
            <Button
              variant="text"
              onClick={currentStep === ImportStep.Enrichment ? handleSkipEnrichment : handleCloseModal}
            >
              {formatMessage({
                id:
                  currentStep >= ImportStep.Complete && currentStep !== ImportStep.Enrichment
                    ? 'general.close'
                    : 'general.cancel',
              })}
            </Button>
          </Box>

          <Divider />

          {/* Stepper */}
          <Box px={4} py={2}>
            <Stepper activeStep={currentStep}>
              {steps.map(label => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          {/* Content */}
          <Stack flexGrow={1} alignItems="center">
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              width="100%"
              maxWidth={1700}
              py={6}
              px={4}
            >
              {importStatus?.status === 'Failed' && (
                <FailedStep errorMessage={importStatus.errorMessage} onClose={handleCloseModal} />
              )}

              {/* Step 0: Upload CSV or Analyzing */}
              {currentStep === ImportStep.Upload &&
                importStatus?.status !== 'Failed' &&
                (isAnalyzing || !isProcessing) && (
                  <UploadStep
                    isAnalyzing={isAnalyzing}
                    importFile={importFile}
                    fileError={fileError}
                    isUploading={isUploading}
                    sessionId={sessionId}
                    uploadDescription={PROVIDER_UPLOAD_DESCRIPTION}
                    onFileChange={handleFileChange}
                    onFileRejected={handleFileRejected}
                    onUpload={handleUpload}
                    onResumeSession={handleResumeSession}
                  />
                )}

              {/* Step 1: Review Mappings */}
              {currentStep === ImportStep.ColumnMatches && importStatus && localMappings && (
                <MappingStep
                  fieldMappings={localMappings}
                  previewRows={importStatus.previewRows ?? []}
                  totalRows={importStatus.totalRows}
                  isApproving={isApprovingCurrent}
                  onToggleMapping={handleToggleMapping}
                  onRetargetMapping={handleRetargetMapping}
                  onSplitMapping={handleSplitMapping}
                  onApprove={handleApproveCurrentMappings}
                  onCancel={handleCloseModal}
                  categoryColumns={
                    categoryPlan?.links.length
                      ? [...new Set(categoryPlan.links.map(l => l.sourceColumnName))]
                      : undefined
                  }
                  unassignedHint={PROVIDER_UNASSIGNED_HINT}
                  renderOverrideExtras={({ mapping, sourceColumns, close }) => (
                    <ConvertToCategoryAction
                      defaultName={sourceColumns[0] ?? mapping.targetFieldName}
                      onConvert={name => {
                        handleConvertColumnToCategory(mapping.id, name)
                        close()
                      }}
                    />
                  )}
                >
                  {categoryPlan && categoryPlan.links.length > 0 && (
                    <CategoryMappingsSection
                      plan={categoryPlan}
                      existingCategoryNames={importStatus.existingCategoryNames ?? null}
                      onChange={plan => {
                        setCategoryPlan(plan)
                        updateCategoryPlan(plan)
                      }}
                      onConvertToColumns={handleConvertCategoryToColumns}
                    />
                  )}
                </MappingStep>
              )}

              {/* Step 2: Processing */}
              {currentStep === ImportStep.Processing && (
                <ProcessingStep
                  progress={getProgress()}
                  realtimeProgress={realtimeProgress}
                  totalRows={importStatus?.totalRows}
                  successfulRows={importStatus?.successfulRows}
                  failedRows={importStatus?.failedRows}
                  errorMessage={importStatus?.errorMessage}
                />
              )}

              {/* Step 3: Validate */}
              {currentStep === ImportStep.Validate && importStatus && sessionId && (
                <ReviewStep
                  sessionId={sessionId}
                  totalRows={importStatus.totalRows}
                  successfulRows={importStatus.successfulRows}
                  validRows={importStatus.validRows}
                  incompleteRows={importStatus.incompleteRows}
                  failedRows={importStatus.failedRows}
                  isImporting={isStartingDedup}
                  isUpdatingRecord={false}
                  formatMessage={formatMessage}
                  onGoBackToMappings={() => setImportStatus({ sessionId, status: 'AwaitingMappingApproval' })}
                  onFinalImport={() => startDeduplication(sessionId)}
                  onClose={handleCloseModal}
                  extraColumns={[
                    {
                      header: 'Categories',
                      render: record => <CategoryPreviewCell preview={record.categoryPreview} />,
                    },
                  ]}
                />
              )}

              {/* Step 4: Deduplication Review */}
              {currentStep === ImportStep.Deduplication && importStatus && sessionId && (
                <DeduplicationReviewStep
                  sessionId={sessionId}
                  importStatus={importStatus}
                  dedupProgress={dedupProgress}
                  isImporting={isImporting}
                  onFinalImport={handleFinalImport}
                  onGoBack={() => setImportStatus({ sessionId, status: 'AwaitingReview' })}
                  assignSearch={props => <ProviderSearchAssign {...props} />}
                  matchDescription={PROVIDER_MATCH_DESCRIPTION}
                  entityNounPlural="providers"
                />
              )}

              {/* Step 5: Importing / Complete */}
              {currentStep === ImportStep.Complete && importStatus && (
                <CompleteStep
                  status={importStatus.status}
                  entityNounPlural="providers"
                  completeButtonLabel={formatMessage({ id: 'import.enrichment.viewProviders' })}
                  onComplete={handleComplete}
                  onEnrichMissingProperties={
                    hasAiEnrichAutomations || newProviderIds.length === 0 ? undefined : handleShowEnrichmentStep
                  }
                  hasEnrichmentAutomation={hasAiEnrichAutomations}
                  importProgress={importProgress}
                  importResult={importResult}
                />
              )}

              {/* Step 6: Enrichment (only if no AI Enrich automation) */}
              {currentStep === ImportStep.Enrichment && importStatus && (
                <EnrichmentStep
                  successfulRows={newProviderIds.length}
                  isScheduling={isEnrichmentScheduling}
                  onScheduleEnrichment={handleScheduleEnrichment}
                  hasProviderIds={newProviderIds.length > 0}
                />
              )}

              {/* Step 7: Enriching View (only if no AI Enrich automation and user scheduled enrichment) */}
              {currentStep === ImportStep.Enriching && importStatus && (
                <ImportEnrichingView errorCount={importStatus.failedRows} onClose={handleComplete} />
              )}
            </Box>
          </Stack>
        </DialogContent>
      </FormProvider>
    </Dialog>
  )
}

export default TabularImportProvidersModal
